import { useCallback, useEffect, useState } from "react";

import { SIGNED_FLOW_TIMEOUT_MS, getSignedFlowExpiration } from "../helpers/signedFlowTimer";

import { useTimeout } from "./useTimeout";

export { getSignedFlowExpiration, SIGNED_FLOW_TIMEOUT_MS };

const formatRemaining = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const useSignedFlowTimer = () => {
  const [expirationTime, setExpirationTime] = useState<number | null>(null);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  const clearTimer = useCallback(() => {
    setExpirationTime(null);
    setRemainingMs(null);
  }, []);

  const handleTimeout = useCallback(() => {
    setRemainingMs(0);
  }, []);

  useTimeout(expirationTime, handleTimeout);
  useEffect(() => {
    if (expirationTime === null) return;
    const tick = () => {
      const ms = expirationTime - Date.now();
      if (ms <= 0) {
        setRemainingMs(0);
        return;
      }
      setRemainingMs(ms);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expirationTime]);

  const startTimer = useCallback((expirationTimestamp?: number) => {
    setExpirationTime(expirationTimestamp !== undefined ? expirationTimestamp : Date.now() + SIGNED_FLOW_TIMEOUT_MS);
  }, []);

  const timerExpired = remainingMs === 0;
  const remainingFormatted = remainingMs !== null && remainingMs > 0 ? formatRemaining(remainingMs) : null;

  return { timerExpired, startTimer, clearTimer, remainingFormatted };
};
